/**
 * @file fieldStyle.tsx
 * @brief The app's "bare" field look for every web form (one switch).
 *
 * @details
 * DES (01-10-2026), after trying it on Profile: "yeh field style theek hai,
 * yeh hi desktop app and web per kartay hain for the whole". The phone's
 * `VZInput labelVariant="bare"`: no fill, border or radius; the LABEL is the
 * placeholder, and once there is a value it is a 13px caption above it
 * (kept in place but invisible while empty, as the phone's
 * `bareLabelHidden` is opacity 0, so rows never jump); value medium 17px.
 *
 * `FormFieldGroup` provides `FieldStyleContext`, and `Input` /
 * `SelectTrigger` inside it read it, so the 75 form groups move with no
 * call-site edits. Search boxes, filter bars and menus are not in a
 * `FormFieldGroup` and keep their look.
 *
 * ⛔ `BARE_FIELDS = false` brings every pill field back.
 *
 * @author DES
 * @date 01-10-2026
 */

import { createContext, useContext } from "react";

export const BARE_FIELDS = true;

/** `error`: the text shown in the label's place, already worded by
 *  `fieldErrorText` (bare only). */
export type FieldStyle = { bare: boolean; label?: string; error?: string };

export const FieldStyleContext = createContext<FieldStyle>({ bare: false });

export function useFieldStyle(): FieldStyle {
  return useContext(FieldStyleContext);
}

/**
 * The phone's bare validation (FRONTEND/src/shared/utils/fieldError.ts, DES
 * 26-09-2026; web 01-10-2026): the error REPLACES the label, in red, as the
 * placeholder while the field is empty and as the caption once it has a
 * value. No line under the field. "Required" becomes "<label> required",
 * "X is required" becomes "X required", anything else is kept.
 */
export function fieldErrorText(label: string | undefined, error: string): string {
  const e = error.trim();
  if (/^(this field is )?required\.?$/i.test(e)) {
    return label ? `${label} required` : "Required";
  }
  return e.replace(/\s+is required\.?$/i, " required");
}

/** The caption when it carries the error: red at full strength (the
 *  caption's 0.65 would wash the red out). */
export const bareCaptionErrorClass = "!text-destructive !opacity-100";

/** The bare value line: transparent, flush left, 17px medium. */
export const bareControlClass =
  "h-auto min-h-6 rounded-none border-0 bg-transparent px-0 py-0 text-[17px] font-medium leading-[21px] text-app-text shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:font-medium placeholder:text-muted-foreground/70";

/**
 * On the bare field group: every other control inside it that wears the
 * field fill (`bg-field`: Title, Gender, Country, Date, Language, Company,
 * person pickers, textareas) goes bare too, without touching each picker.
 * Menus and popovers are portalled out of the group, so they keep theirs.
 */
export const bareDescendantsClass =
  "[&_.bg-field]:!h-auto [&_.bg-field]:!min-h-6 [&_.bg-field]:!rounded-none [&_.bg-field]:!border-0 [&_.bg-field]:!bg-transparent [&_.bg-field]:!px-0 [&_.bg-field]:!py-0 [&_.bg-field]:!text-[17px] [&_.bg-field]:!font-medium [&_.bg-field]:!shadow-none [&_.bg-field]:!ring-0 [&_.bg-field]:!ring-offset-0";

/** The hairline under each bare field, as the phone's `bareDivider`. */
export const bareFieldLineClass = "border-b border-app-text/10 pb-2.5";

/** The caption over a bare value: 13px medium, 0.65 opacity. */
export const bareCaptionClass =
  "mb-[3px] block text-[13px] font-medium leading-4 text-muted-foreground opacity-[0.65]";
